import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from './AuthProvider';
import { authService } from '../services/authService';
import '../styles/MyBookings.css';

interface Booking {
  id: number;
  serviceType: string;
  consultationType: string;
  date: string;
  time: string;
  duration: number;
  location: string | null;
  caseDescription: string | null;
  status: string;
  amount: string;
  createdAt: string;
  updatedAt: string;
  lawyer: {
    id: number;
    name: string;
    specialization: string;
    rating: number;
    profileImage: string | null;
  } | null;
  payment: {
    amount: string;
    status: string;
    method: string | null;
    paymentId: string | null;
    orderId: string | null;
  };
  user: {
    name: string;
    email: string;
    phone: string;
  };
}

interface MyBookingsProps {
  isOpen: boolean;
  onClose: () => void;
}

const MyBookings: React.FC<MyBookingsProps> = ({ isOpen, onClose }) => {
  const { user } = useContext(AuthContext) || {};
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 10,
    offset: 0,
    hasMore: false
  });

  const statusOptions = [
    { value: 'all', label: 'All Bookings' },
    { value: 'pending', label: 'Pending' },
    { value: 'pending_verification', label: 'Pending Verification' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const fetchBookings = async (status: string = 'all', offset: number = 0) => {
    if (!user) return;

    const token = authService.getAuthToken();
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      let url = `http://localhost:5000/api/v1/bookings/my-bookings?limit=${pagination.limit}&offset=${offset}`;
      if (status !== 'all') {
        url += `&status=${status}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch bookings');
      }

      const data = await response.json();
      
      if (data.success) {
        setBookings(offset === 0 ? data.data.bookings : [...bookings, ...data.data.bookings]);
        setPagination(data.data.pagination);
      } else {
        throw new Error(data.message || 'Failed to fetch bookings');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && user) {
      fetchBookings(selectedStatus);
    }
  }, [isOpen, user, selectedStatus]);

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
    setBookings([]);
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const loadMoreBookings = () => {
    if (pagination.hasMore && !loading) {
      fetchBookings(selectedStatus, pagination.offset + pagination.limit);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return timeString.substring(0, 5);
  };

  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: { class: string; label: string } } = {
      pending: { class: 'status-pending', label: 'Pending' },
      pending_verification: { class: 'status-pending-verification', label: 'Pending Verification' },
      confirmed: { class: 'status-confirmed', label: 'Confirmed' },
      completed: { class: 'status-completed', label: 'Completed' },
      cancelled: { class: 'status-cancelled', label: 'Cancelled' }
    };

    const statusInfo = statusMap[status] || { class: 'status-unknown', label: status };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  const getServiceTypeLabel = (serviceType: string) => {
    const serviceMap: { [key: string]: string } = {
      civil: 'Civil Law',
      criminal: 'Criminal Law',
      matrimonial: 'Matrimonial Law',
      arbitration: 'Arbitration',
      mediation: 'Mediation',
      negotiable: 'Legal Consultation'
    };
    return serviceMap[serviceType] || serviceType.charAt(0).toUpperCase() + serviceType.slice(1);
  };

  if (!isOpen) return null;

  return (
    <div className="my-bookings-overlay" onClick={onClose}>
      <div className="my-bookings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="my-bookings-header">
          <h2>My Bookings</h2>
          <button className="close-button" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="my-bookings-controls">
          <div className="status-filter">
            <label htmlFor="status-select">Filter by Status:</label>
            <select 
              id="status-select"
              value={selectedStatus} 
              onChange={(e) => handleStatusChange(e.target.value)}
              className="status-select"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="my-bookings-content">
          {loading && bookings.length === 0 ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading your bookings...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <i className="fas fa-exclamation-triangle"></i>
              <p>{error}</p>
              <button onClick={() => fetchBookings(selectedStatus)} className="retry-button">
                Try Again
              </button>
            </div>
          ) : bookings.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-calendar-alt"></i>
              <h3>No Bookings Found</h3>
              <p>You haven't made any bookings yet.</p>
            </div>
          ) : (
            <>
              <div className="bookings-list">
                {bookings.map((booking) => (
                  <div key={booking.id} className="booking-card">
                    <div className="booking-header">
                      <div className="booking-id">Booking #{booking.id}</div>
                      {getStatusBadge(booking.status)}
                    </div>
                    
                    <div className="booking-details">
                      <div className="detail-row">
                        <div className="detail-item">
                          <i className="fas fa-gavel"></i>
                          <div>
                            <span className="label">Service</span>
                            <span className="value">{getServiceTypeLabel(booking.serviceType)}</span>
                          </div>
                        </div>
                        <div className="detail-item">
                          <i className="fas fa-video"></i>
                          <div>
                            <span className="label">Type</span>
                            <span className="value">{booking.consultationType.charAt(0).toUpperCase() + booking.consultationType.slice(1)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="detail-row">
                        <div className="detail-item">
                          <i className="fas fa-calendar"></i>
                          <div>
                            <span className="label">Date</span>
                            <span className="value">{formatDate(booking.date)}</span>
                          </div>
                        </div>
                        <div className="detail-item">
                          <i className="fas fa-clock"></i>
                          <div>
                            <span className="label">Time</span>
                            <span className="value">{formatTime(booking.time)} ({booking.duration} mins)</span>
                          </div>
                        </div>
                      </div>

                      {booking.location && (
                        <div className="detail-row">
                          <div className="detail-item full-width">
                            <i className="fas fa-map-marker-alt"></i>
                            <div>
                              <span className="label">Location</span>
                              <span className="value">{booking.location}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {booking.caseDescription && (
                        <div className="detail-row">
                          <div className="detail-item full-width">
                            <i className="fas fa-file-alt"></i>
                            <div>
                              <span className="label">Case Description</span>
                              <span className="value description">{booking.caseDescription}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {booking.lawyer && (
                        <div className="lawyer-info">
                          <h4>Assigned Lawyer</h4>
                          <div className="lawyer-details">
                            <div className="lawyer-name">{booking.lawyer.name}</div>
                            <div className="lawyer-specialization">{booking.lawyer.specialization}</div>
                            <div className="lawyer-rating">
                              <i className="fas fa-star"></i>
                              {booking.lawyer.rating}/5
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="payment-info">
                        <div className="amount">₹{booking.amount}</div>
                        <div className="payment-status">
                          Payment: <span className={`payment-${booking.payment.status}`}>{booking.payment.status}</span>
                        </div>
                      </div>
                    </div>

                    <div className="booking-footer">
                      <span className="created-date">
                        Created on {formatDate(booking.createdAt)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {pagination.hasMore && (
                <div className="load-more-container">
                  <button 
                    onClick={loadMoreBookings} 
                    disabled={loading}
                    className="load-more-button"
                  >
                    {loading ? 'Loading...' : 'Load More Bookings'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyBookings;